import { FC, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ActionLinkBack } from '@features/workspace/components/controls/action-link';
import history from '@app/services/history';
import ScrollToTop from '@features/workspace/components/shared/scroll-to-top';

/**
 *
 *  Use cases either:
 *     <CreateEditLayout><ContentComponent/></EditLayout>
 *  or
 *     <CreateEditLayout asTabs={[
 *         { title: 'Tab 1', content: <ContentComponent/> }
 *     ]} />
 *
 * @param children
 * @param backUri
 * @param title
 * @param asTabs
 * @param saveButtonText
 * @param cancelButtonText
 * @param onSave
 * @param onCancel
 * @param onBack
 * @param errors optionally for validation errors summary
 * @constructor
 */

const CreateEditLayout: FC<any> = ({
  children,
  backUri,
  title,
  asTabs,
  saveButtonText = 'Save',
  cancelButtonText = 'Cancel',
  onSave,
  onCancel,
  onBack,
  errors = null,
}) => {
  const onInternalBack = () => {
    if (onBack) {
      onBack();
    } else {
      history.push(backUri);
    }
  };

  const onInternalCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      history.push(backUri);
    }
  };

  const onInternalSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const [activeTab, setActiveTab] = useState(0);

  const getKeyByTitle = (currentTitle: string) =>
    currentTitle.toLowerCase().replaceAll(' ', '-');

  const errorsSummaryList = ((errors && Object.values(errors)) || []).reduce(
    (acc: Array<string>, err: any) => {
      if (!err.message) {
        Object.values(err).forEach((subErr: any) => {
          acc.push(subErr.message);
        });
      } else {
        acc.push(err.message);
      }

      return acc;
    },
    []
  );

  return (
    <ScrollToTop>
      <Box display="flex" flexDirection="column" width="100%">
        <Box alignItems="center" display="flex">
          {backUri && (
            <Box display="flex" mr={2}>
              <ActionLinkBack onClick={onInternalBack}>Back</ActionLinkBack>
            </Box>
          )}
          {title && (
            <Box display="flex">
              <Typography variant="legend">{title}</Typography>
            </Box>
          )}
        </Box>
        {asTabs && (
          <Box display="flex" flexGrow={1} mt={2.5}>
            <Tabs
              value={activeTab}
              onChange={(event: any, value: number) => setActiveTab(value)}
            >
              {asTabs.map((tab: any) => (
                <Tab
                  key={`${getKeyByTitle(tab.title)}-tab`}
                  label={tab.title}
                />
              ))}
            </Tabs>
          </Box>
        )}
        <Box display="flex" flexGrow={1} mt={2.5}>
          <Card style={{ width: '100%' }}>
            <CardContent>
              <Box display="flex" flexGrow={1}>
                {asTabs &&
                  asTabs.map((tab: any, iTab: number) => (
                    <Box
                      key={`${getKeyByTitle(tab.title)}-tab-panel`}
                      display={iTab === activeTab ? 'flex' : 'none'}
                      flexGrow={1}
                    >
                      {tab.content}
                    </Box>
                  ))}
                {!asTabs && children}
              </Box>
              {/* Errors summary */}
              {!!errorsSummaryList.length && (
                <Box
                  color="#f44336"
                  display="flex"
                  flexDirection="column"
                  mt={3}
                >
                  <Typography variant="subtitle2">
                    Please fix following errors:
                  </Typography>
                  <ul
                    style={{
                      fontSize: '0.75rem',
                      lineHeight: '1.5rem',
                      paddingLeft: '1rem',
                    }}
                  >
                    {errorsSummaryList.map(
                      (errMessage: string, iErr: number) => (
                        <li key={`${iErr}-${errMessage}`}>{errMessage}</li>
                      )
                    )}
                  </ul>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
        <Box
          alignItems="center"
          display="flex"
          justifyContent="flex-end"
          py={2}
        >
          <Box>
            <Button
              color="secondary"
              size="large"
              variant="contained"
              onClick={onInternalCancel}
            >
              {cancelButtonText}
            </Button>
          </Box>
          <Box ml={1}>
            <Button size="large" variant="contained" onClick={onInternalSave}>
              {saveButtonText}
            </Button>
          </Box>
        </Box>
      </Box>
    </ScrollToTop>
  );
};

export default CreateEditLayout;
